import { useTranslation } from 'react-i18next';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getXAxisDomainForHistoricGraph, minMaxAxisDomain } from '@/components/Charts/utils';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMembershipType } from '@/hooks/useMembershipType';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { formatUnit } from '@/utils/formatting';
import { printYear } from '@/utils/formatting/date';

import { NoData } from '../../../components/NoData';
import { Tile, TileCapsuleContent, TileCta, TileTitle } from './Tile';

export const WaterTile = () => {
  const { t } = useTranslation();
  const pathSet =
    useMembershipType() === MembershipWithOrganizationTypeEnum.land_steward ? paths.landSteward : paths.buyer;

  const plotGroupId = usePlotGroupId();

  const historicWhcPerHaGraphFact = R1FactType.r1_historic_water_and_soil_moisture_per_ha_graph;
  const { [historicWhcPerHaGraphFact]: historicWhcPerHaGraph } =
    useOrganizationIndicatorGraphFact<HistoricWaterAndSoilMoistureGraphData[]>(historicWhcPerHaGraphFact);
  const historicWhcPerHaGraphFormatted = historicWhcPerHaGraph?.value
    ?.filter((d) => d.name === 'water_holding_capacity_per_ha')
    // @ts-ignore typescript does not allow arithmetic operations between date objects
    .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
    .map((d) => ({ ...d, date: new Date(d.date).getTime() }));

  const whcLatestAnalysisFact = R1FactType.r1_water_holding_capacity_latest_analysis_per_ha;
  const { [whcLatestAnalysisFact]: whcLatestAnalysis } =
    useOrganizationIndicatorReportingFact<number>(whcLatestAnalysisFact);

  const chartData = historicWhcPerHaGraphFormatted;

  let whcLatestAnalysisDisplay = `${useDisplayNumber(whcLatestAnalysis?.value ?? 0)} ${formatUnit(UnitEnum['m^3/ha'])}`;
  whcLatestAnalysisDisplay = !isNullish(whcLatestAnalysis?.value) ? whcLatestAnalysisDisplay : '-';

  const noChartData = !chartData?.length;

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart chartData={chartData} />}
      title={<TileTitle>{t('global.analysis.water')}</TileTitle>}
      capsuleContent={<TileCapsuleContent>{whcLatestAnalysisDisplay}</TileCapsuleContent>}
      cta={
        <TileCta to={buildPath(pathSet.waterDetails, { queryParams: { plotGroupId } })}>
          {t('global.analysis.viewDetails')}
        </TileCta>
      }
    />
  );
};

type HistoricWaterAndSoilMoistureGraphData = {
  date: number;
  name: 'water_holding_capacity_per_ha';
  value: number;
  unit: string;
};
const Chart = ({ chartData }: { chartData: HistoricWaterAndSoilMoistureGraphData[] }) => {
  const firstTick = chartData.at(0)?.date;
  const lastTick = chartData.at(-1)?.date;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick!, lastTick!) ?? [
    new Date().getFullYear(),
    new Date().getFullYear(),
  ];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startYear = xTicksDomain[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endYear = xTicksDomain[1]!;
  const ticksRange = Array(endYear - startYear + 1).keys();
  const xTicks = Array.from(ticksRange).map((x) => new Date(`12-31-${startYear + x}`).getTime());
  const yearsSubset = xTicks.filter((_, i) => i % 2 !== 0 && i !== xTicks.length - 1);

  return (
    <ResponsiveContainer data-testid='water-historic-chart-tile' width='100%' height={80}>
      <AreaChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id='waterHoldingCapacity' x1='0' y1='-0.85' x2='0' y2='1'>
            <stop offset='0%' stopColor='#6ACDE1' stopOpacity={1} />
            <stop offset='100%' stopColor='#6ACDE1' stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          isAnimationActive={false}
          type='stepBefore'
          dataKey='value'
          stroke='#71A7B2'
          strokeWidth={2}
          fill='url(#waterHoldingCapacity)'
          fillOpacity={1}
        />
        <XAxis
          dataKey='date'
          scale='time'
          type='number'
          domain={['dataMin', 'dataMax']}
          ticks={yearsSubset}
          tickFormatter={printYear}
          axisLine={false}
          tickLine={false}
        />
        <YAxis hide dataKey='value' type='number' scale='sequential' width={0} domain={minMaxAxisDomain(0, 1.2)} />
      </AreaChart>
    </ResponsiveContainer>
  );
};
